import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "../../utils/config";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate } from "../../utils/Utilitats";

/**
 * Peces comunes dels CRUD de Recursos (Vehicles, Eines, Magatzems).
 * Imatges: mateix sistema que Productes (Imatgeproducte.php, carpeta imatgesprod/,
 * només es desa el nom del fitxer, miniatura reduïda a 300x300 JPEG al navegador).
 */

const MAX_AMPLE = 300;
const MAX_ALT = 300;

export const urlImatgeRecurs = (nom) => (nom ? `${API_URL}/imatgesprod/${nom}` : "");

export const avuiISO = () => {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
};

// Crida POST JSON autenticada. Retorna { ok, data, message, silenci }.
// "silenci" = sessió caducada (ja s'ha avisat i redirigit a fetchAutenticat).
export async function cridaRecurs(endpoint, body = {}) {
  try {
    const resposta = await fetchAutenticat(`${API_URL}/${endpoint}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    const data = await resposta.json();
    if (!data.success) {
      return { ok: false, data, message: data.message || "No s'ha pogut completar l'operació." };
    }
    return { ok: true, data };
  } catch (error) {
    if (error instanceof SessioCaducadaError) return { ok: false, silenci: true };
    console.error(`Error cridant ${endpoint}:`, error);
    return { ok: false, message: "No s'ha pogut connectar amb el servidor." };
  }
}

function redimensionarImatge(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No s'ha pogut llegir la imatge."));
    reader.onloadend = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("El fitxer no és una imatge vàlida."));
      img.onload = () => {
        let ample = img.width;
        let alt = img.height;
        if (ample > alt) {
          if (ample > MAX_AMPLE) {
            alt *= MAX_AMPLE / ample;
            ample = MAX_AMPLE;
          }
        } else if (alt > MAX_ALT) {
          ample *= MAX_ALT / alt;
          alt = MAX_ALT;
        }
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(ample));
        canvas.height = Math.max(1, Math.round(alt));
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff"; // els PNG transparents no es converteixen en negre
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg");
        const nom = file.name.replace(/\.[^/.]+$/, ".jpg");
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("No s'ha pogut preparar la imatge."));
              return;
            }
            resolve({ dataUrl, file: new File([blob], nom, { type: "image/jpeg" }) });
          },
          "image/jpeg",
          0.9
        );
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

async function pujarImatge(file) {
  const formData = new FormData();
  formData.append("imatge", file);
  const resposta = await fetch(`${API_URL}/Imatgeproducte.php`, { method: "POST", body: formData });
  const data = await resposta.json();
  if (!data.success) throw new Error(data.message || "Error en pujar la imatge.");
  return data.imatge;
}

/**
 * Estat de la imatge d'un registre dins la fitxa.
 * - reinicia(nom): carrega la imatge actual del registre.
 * - triar(event): escull un fitxer nou (previsualització immediata).
 * - treure(): deixa el registre sense imatge.
 * - desa(): puja el fitxer nou si cal i retorna el nom a desar (o null).
 */
export function useImatgeRecurs() {
  const [nom, setNom] = useState("");
  const [fitxer, setFitxer] = useState(null);
  const [previsualitzacio, setPrevisualitzacio] = useState("");
  const inputRef = useRef(null);

  const reinicia = useCallback((nomInicial) => {
    setNom(nomInicial || "");
    setFitxer(null);
    setPrevisualitzacio("");
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const triar = useCallback(async (event) => {
    const escollit = event.target.files && event.target.files[0];
    if (!escollit) return;
    try {
      const resultat = await redimensionarImatge(escollit);
      setFitxer(resultat.file);
      setPrevisualitzacio(resultat.dataUrl);
    } catch (error) {
      alert(error.message);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, []);

  const treure = useCallback(() => {
    setNom("");
    setFitxer(null);
    setPrevisualitzacio("");
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const desa = useCallback(async () => {
    if (fitxer) return pujarImatge(fitxer);
    return nom || null;
  }, [fitxer, nom]);

  return {
    url: previsualitzacio || urlImatgeRecurs(nom),
    teImatge: Boolean(previsualitzacio || nom),
    inputRef,
    reinicia,
    triar,
    treure,
    desa,
  };
}

export function MiniaturaRecurs({ imatge, alt = "", icona = "📷" }) {
  return (
    <span className="dv2-thumb" aria-hidden={imatge ? undefined : "true"}>
      {imatge ? <img src={urlImatgeRecurs(imatge)} alt={alt} loading="lazy" /> : icona}
    </span>
  );
}

export function CampImatge({ imatge, readOnly, alt, icona = "📷" }) {
  return (
    <div className="dv2-imageField">
      {imatge.teImatge ? (
        <img src={imatge.url} alt={alt} className="dv2-imagePreview" />
      ) : (
        <span className="dv2-thumb" aria-hidden="true">
          {icona}
        </span>
      )}
      {readOnly ? (
        !imatge.teImatge && <span className="dv2-value">Sense imatge</span>
      ) : (
        <>
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="dv2-input"
            style={{ maxWidth: 320 }}
            onChange={imatge.triar}
            ref={imatge.inputRef}
          />
          {imatge.teImatge && (
            <button type="button" className="dv2-btn dv2-btn--compact" onClick={imatge.treure}>
              Treure imatge
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Camp etiquetat amb la mida semàntica del patró dv2 (curt | mig | llarg | ample).
export function Camp({ label, required, readOnly, mida = "mig", children }) {
  const extra =
    mida === "ample"
      ? "dv2-field--ample"
      : mida === "llarg"
      ? "dv2-field--llarg"
      : mida === "curt"
      ? "dv2-field--curt"
      : "dv2-field--mig";
  return (
    <label className={`dv2-field ${extra}`}>
      <span className={`dv2-label ${required && !readOnly ? "dv2-required" : ""}`}>{label}</span>
      {children}
    </label>
  );
}

export function Valor({ children }) {
  return <span className="dv2-value">{children === null || children === undefined || children === "" ? "—" : children}</span>;
}

export function InterruptorActiu({ valor, onChange, readOnly }) {
  if (readOnly) return <span className={`dv2-chip ${valor ? "dv2-chip--ok" : ""}`}>{valor ? "Sí" : "No"}</span>;
  return (
    <span className="dv2-toggleRow">
      <span className="dv2-toggle">
        <input type="checkbox" checked={!!valor} onChange={(e) => onChange(e.target.checked)} />
        <span className="dv2-toggle-track" />
      </span>
      {valor ? "Sí" : "No"}
    </span>
  );
}

export function ChipActiu({ actiu, femeni = false }) {
  const [si, no] = femeni ? ["Activa", "Inactiva"] : ["Actiu", "Inactiu"];
  return actiu ? <span className="dv2-chip dv2-chip--ok">{si}</span> : <span className="dv2-chip">{no}</span>;
}

// Estat d'una data de venciment respecte a avui: "caducat" | "proper" | null.
export function estatVenciment(dataISO, diesAvis = 30) {
  if (!dataISO) return null;
  const avui = avuiISO();
  if (dataISO < avui) return "caducat";
  const limit = new Date();
  limit.setDate(limit.getDate() + diesAvis);
  const mes = String(limit.getMonth() + 1).padStart(2, "0");
  const dia = String(limit.getDate()).padStart(2, "0");
  return dataISO <= `${limit.getFullYear()}-${mes}-${dia}` ? "proper" : null;
}

// Data formatada amb marca visual si ha vençut o venç aviat.
export function DataVenciment({ data, diesAvis = 30 }) {
  if (!data) return <>—</>;
  const estat = estatVenciment(data, diesAvis);
  if (!estat) return <>{formatDate(data)}</>;
  return (
    <span
      className={`dv2-chip ${estat === "caducat" ? "dv2-chip--danger" : "dv2-chip--warn"}`}
      title={estat === "caducat" ? "Data vençuda" : `Venç en menys de ${diesAvis} dies`}
    >
      {formatDate(data)}
    </span>
  );
}

// Tanca el menú desplegable quan es fa clic fora (patró de les llistes dv2).
export function useTancarMenuFora(setMenuObert) {
  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, [setMenuObert]);
}
